/**
 * EF-IMPORT-AND-ONBOARDING-GUIDANCE-V1
 * 
 * CrewOnboardingHelpPanel - Guidance panel for crew onboarding workflow
 * 
 * Features:
 * - Clear workflow explanation
 * - Onboarding email contents
 * - Admin testing guidance
 * - Collapsible sections
 */

import React, { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "../ui/collapsible";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  ChevronDown,
  ChevronUp,
  HelpCircle,
  UserPlus,
  Send,
  FileCheck,
  CheckCircle,
  Users,
  Mail,
  Smartphone,
  FileText,
  Shield,
  AlertTriangle,
  Info,
  ListChecks,
  Globe,
  Lock,
  Home,
  IdCard,
} from "lucide-react";
import { cn } from "../../lib/utils";

// Workflow step component
const WorkflowStep = ({ number, icon: Icon, title, description, highlight = false }) => (
  <div className={cn(
    "flex gap-3 p-3 rounded-lg transition-colors",
    highlight ? "bg-primary/10 border border-primary/20" : "bg-surface-base/30"
  )}>
    <div className={cn(
      "w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-bold",
      highlight ? "bg-primary text-primary-foreground" : "bg-surface-elevated text-muted-foreground"
    )}>
      {number}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={cn("h-4 w-4", highlight ? "text-primary" : "text-muted-foreground")} />
        <span className="font-medium text-sm">{title}</span>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        {description}
      </p>
    </div>
  </div>
);

// Option card component
const OptionCard = ({ option, icon: Icon, title, description }) => (
  <div className="p-3 bg-surface-elevated/30 border border-white/5 rounded-lg">
    <div className="flex items-center gap-2 mb-2">
      <Badge variant="outline" className="text-[10px]">{option}</Badge>
      <Icon className="h-4 w-4 text-primary" />
      <span className="font-medium text-sm">{title}</span>
    </div>
    <p className="text-xs text-muted-foreground pl-8">{description}</p>
  </div>
);

export function CrewOnboardingHelpPanel({ className, defaultOpen = false, variant = "full" }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  // Compact version for inline use in forms
  if (variant === "compact") {
    return (
      <div className={cn(
        "p-3 bg-blue-500/5 border border-blue-500/10 rounded-lg",
        className
      )}>
        <div className="flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
          <div className="text-sm text-blue-300/90">
            <strong>Onboarding invitation includes:</strong> portal link, user ID (email), 
            temporary password, required profile fields, required documents list, 
            and proof of address rules.
          </div>
        </div>
      </div>
    );
  }
  
  // Minimal version for button tooltips
  if (variant === "minimal") {
    return (
      <div className={cn("text-xs space-y-1", className)}>
        <p><strong>Required fields:</strong> LASTNAME, Firstname, Email, Mobile</p>
        <p><strong>Crew receives:</strong> Portal link + temporary password</p>
        <p><strong>Options:</strong> Complete online or reply with documents</p>
      </div>
    );
  }
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn("w-full", className)}>
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="w-full justify-between p-3 h-auto bg-surface-elevated/30 hover:bg-surface-elevated/50 border border-white/5 rounded-lg"
          data-testid="crew-onboarding-help-toggle"
        >
          <div className="flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-primary" />
            <span className="font-medium">How crew onboarding works</span>
          </div>
          {isOpen ? (
            <ChevronUp className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          )}
        </Button>
      </CollapsibleTrigger>
      
      <CollapsibleContent className="mt-3">
        <div className="p-4 bg-surface-elevated/20 border border-white/5 rounded-lg space-y-5">
          
          {/* Workflow Steps */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
              <ListChecks className="h-4 w-4 text-primary" />
              Onboarding Workflow
            </h4>
            
            <WorkflowStep
              number="1"
              icon={UserPlus}
              title="Create Crew Profile"
              description="Admin enters minimum required data: LASTNAME, Firstname, Email, Mobile"
            />
            
            <WorkflowStep
              number="2"
              icon={Send}
              title="Send Onboarding Invitation"
              description="Click 'Send Onboarding Invitation' to email the crew member with portal access"
              highlight={true}
            />
            
            {/* Crew Response Options */}
            <div className="ml-10 space-y-2 my-3">
              <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                Crew can choose:
              </p>
              <OptionCard
                option="A"
                icon={Globe}
                title="Complete in Crew Portal / PWA"
                description="Fill profile online and upload documents directly"
              />
              <OptionCard
                option="B"
                icon={Mail}
                title="Reply with Documents"
                description="Reply to onboarding email with requested information and attached documents"
              />
            </div>
            
            <WorkflowStep
              number="3"
              icon={FileCheck}
              title="Admin Reviews Documents"
              description="Review intake packet, verify document authenticity, and check completeness"
            />
            
            <WorkflowStep
              number="4"
              icon={CheckCircle}
              title="Activate Crew Member"
              description="Once verified, activate the crew member to make them available for shift assignment"
            />
          </div>
          
          {/* Proof of Address Rule */}
          <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
            <div className="text-sm">
              <strong className="text-amber-300">Proof of Address Rule:</strong>
              <span className="text-amber-200/80 ml-1">
                If a proof of address is not in the crew member's name, the ID of the named 
                person (e.g., spouse, parent) must also be provided.
              </span>
            </div>
          </div>
          
          {/* What the Email Contains */}
          <div className="pt-3 border-t border-white/5">
            <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
              <Mail className="h-4 w-4 text-primary" />
              Onboarding Email Contents
            </h4>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Globe className="h-3 w-3 text-emerald-400" />
                <span>Portal link</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Mail className="h-3 w-3 text-emerald-400" />
                <span>User ID (email)</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Lock className="h-3 w-3 text-emerald-400" />
                <span>Temporary password</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <FileText className="h-3 w-3 text-emerald-400" />
                <span>Required profile fields</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <IdCard className="h-3 w-3 text-emerald-400" />
                <span>Required documents list</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Home className="h-3 w-3 text-emerald-400" />
                <span>Proof of address rules</span>
              </div>
            </div>
          </div>
          
          {/* Admin Testing Guidance */}
          <div className="pt-3 border-t border-white/5">
            <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
              <Shield className="h-4 w-4 text-primary" />
              Recommended Testing Order
            </h4>
            <ol className="space-y-1 text-xs text-muted-foreground pl-4 list-decimal">
              <li>Create crew profile with required fields</li>
              <li>Send onboarding invitation</li>
              <li>Upload or receive documents via email</li>
              <li>Review intake packet in Crew Intake</li>
              <li>Verify documents and OCR data</li>
              <li>Activate crew member</li>
              <li>Assign crew to shift</li>
            </ol>
          </div>
          
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// Inline help text for send onboarding button
export function OnboardingButtonHelp() {
  return (
    <div className="text-[10px] text-muted-foreground mt-1.5 space-y-0.5">
      <p>Sends portal link, temp password, and required documents list</p>
    </div>
  );
}

export default CrewOnboardingHelpPanel;
